import { ExcelExport } from '@progress/kendo-react-excel-export'
import { GridColumn} from '@progress/kendo-react-grid'
import { FailureMsgCell } from 'components/content-pool/kendocells/failureMsgCell'
import { DateTimeCell } from 'components/gridcells/datetime'
import { HeaderComponent } from 'components/header/component'
import { SearchIcon } from 'components/icons/searchicon'
import { Loading } from 'components/loading'
import { RasaContext } from 'context'
import * as GenericRedux from 'generic/genericRedux'
import { RasaDataGrid } from 'generic/rasaDataGrid'
import * as React from 'react'
import { Button } from 'reactstrap'
import html2canvas from 'html2canvas'
import jsPDF from 'jspdf'
import { Chart, ChartSeries, ChartSeriesItem, ChartCategoryAxis, ChartCategoryAxisItem, ChartTitle } from '@progress/kendo-react-charts'
import * as Constants from './constants'
import { PDFReportHeader } from './PDFReportHeader'
import { RasaAnalyticsComponent } from './rasa-analytics-component'
import './styles.css'

export class SourcesRunReportComponent extends RasaAnalyticsComponent<any, any> {
  public static contextType = RasaContext;
  constructor(props) {
    super(props)
  }

  public componentDidMount() {
    this.context.user.init().then(({person, activeCommunity}) => {
    this.setState({
      issuesLoading: false,
      communityId: activeCommunity.communityId,
      selectedIssue: {key: 'Select a Newsletter', value: -1},
      })
    })
  }

  public render() {
    return(
      <div className="analytics-component">
        <HeaderComponent
          title={'REPORTS'}
          subTitle={'Newsletter Source Report'}
          description={['This report shows how many active articles from a past send were from each source.']}
        />
        {this.state.issuesLoading ?
          <Loading size="64" /> :
          (!this.props.hideHeader) ?
          <div>
            <SourceReportTableComponent 
              dateRangeLabel="All Time"
            />
          </div>
          :
          <div>
            <p className="no-data-tag">{Constants.NO_DATA_COPY}</p>
          </div>
        }
    </div>
    )
  }
}

interface SourceReportsState {
  loaded?: boolean,
  sources?: any[],
  search?: string,
}

interface SourcesReportsProps {
  sourceName?: any,
  issue_id?: string,
  dateRangeLabel?: string,
}

class SourceReportTableComponent extends React.Component<SourcesReportsProps,
  SourceReportsState> {
  public static contextType = RasaContext
  private xlsxExport: any = null
  private pdfExportRef: React.RefObject<HTMLDivElement> = React.createRef()
  constructor(props: any) {
    super(props)
    this.state = {
      loaded: false,
      sources: [],
      search: '',
    }
  }

  public exportToPDF = async () => {
    if (!this.pdfExportRef.current) return

    try {
      const element = this.pdfExportRef.current

      // Temporarily add PDF export mode class to show PDF elements
      element.classList.add('pdf-export-mode')

      // Wait a brief moment for styles to apply
      await new Promise(resolve => setTimeout(resolve, 100))

      const pdf = new jsPDF('l', 'mm', 'a4') // landscape orientation
      const pdfWidth = pdf.internal.pageSize.getWidth()
      const pdfHeight = pdf.internal.pageSize.getHeight()

      // Capture header and chart separately (Page 1)
      const headerChart = element.querySelector('.pdf-header-chart-section')
      if (headerChart) {
        const headerCanvas = await html2canvas(headerChart as HTMLElement, {
          height: headerChart.scrollHeight,
          width: headerChart.scrollWidth,
          useCORS: true,
          scale: 2
        })

        const headerImgData = headerCanvas.toDataURL('image/png')
        const headerRatio = Math.min(pdfWidth / headerCanvas.width, pdfHeight / headerCanvas.height)
        const headerImgX = (pdfWidth - headerCanvas.width * headerRatio) / 2

        pdf.addImage(headerImgData, 'PNG', headerImgX, 10, headerCanvas.width * headerRatio, headerCanvas.height * headerRatio)
      }

      // Capture table separately (Page 2+)
      if (this.state.sources && this.state.sources.length > 0) {
        const itemsPerPage = 12
        const totalPages = Math.ceil(this.state.sources.length / itemsPerPage)

        for (let page = 0; page < totalPages; page++) {
          pdf.addPage()

          // Add page header
          pdf.setFontSize(16)
          pdf.text(`Sources Report - Page ${page + 2} of ${totalPages + 1}`, 20, 20)
          pdf.setFontSize(12)
          pdf.text(`Showing entries ${page * itemsPerPage + 1} - ${Math.min((page + 1) * itemsPerPage, this.state.sources.length)} of ${this.state.sources.length}`, 20, 30)

          // Create table data for this page
          const pageData = this.state.sources.slice(page * itemsPerPage, (page + 1) * itemsPerPage)

          // Simple table rendering
          let yPosition = 50
          const rowHeight = 8
          const colPositions = [20, 80, 120, 160, 200, 240]

          // Table headers
          pdf.setFontSize(9)
          pdf.setFont(undefined, 'bold')
          pdf.text('Identifier', colPositions[0], yPosition)
          pdf.text('Type', colPositions[1], yPosition)
          pdf.text('Name', colPositions[2], yPosition)
          pdf.text('Subscriptions', colPositions[3], yPosition)
          pdf.text('Last Post', colPositions[4], yPosition)
          pdf.text('Last Success', colPositions[5], yPosition)
          yPosition += rowHeight + 2

          // Table rows
          pdf.setFont(undefined, 'normal')
          pageData.forEach((item: any) => {
            if (yPosition > pdfHeight - 20) return // Stop if near bottom of page

            pdf.text(item.identifier ? item.identifier.substring(0, 25) : 'N/A', colPositions[0], yPosition)
            pdf.text(item.type ? item.type.substring(0, 15) : 'N/A', colPositions[1], yPosition)
            pdf.text(item.name ? item.name.substring(0, 20) : 'N/A', colPositions[2], yPosition)
            pdf.text((item.total_subscription || 0).toString(), colPositions[3], yPosition)
            pdf.text(item.last_post_date ? new Date(item.last_post_date).toLocaleDateString() : 'N/A', colPositions[4], yPosition)
            pdf.text(item.last_success ? new Date(item.last_success).toLocaleDateString() : 'N/A', colPositions[5], yPosition)
            yPosition += rowHeight
          })
        }
      }

      // Remove PDF export mode class
      element.classList.remove('pdf-export-mode')

      pdf.save('sources-report.pdf')
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error generating PDF:', error)
      // Make sure to remove the class even if there's an error
      if (this.pdfExportRef.current) {
        this.pdfExportRef.current.classList.remove('pdf-export-mode')
      }
    }
  }

  public render = () => {
   return <div>
    <div className="articles-chart">
      <div className="articles">
      <div className="search-bar">
              <label>
                <SearchIcon/>
                <input type="text"
                  placeholder="Search by title, description, source_name or url"
                  onChange={(e) => this.setSearch(e.target.value)}/>
              </label>
            </div>
        <div className="articles-export-buttons">
          <Button
            onClick={() => this.xlsxExport.save()}>
            Export xlsx
          </Button>
          <Button
            color="secondary"
            onClick={this.exportToPDF}>
            Export PDF
          </Button>
        </div>
        <div ref={this.pdfExportRef}>
          <div className="pdf-header-chart-section">
            <PDFReportHeader
              title="Newsletter Source Report"
              dateRange={this.props.dateRangeLabel || 'All Time'}
              fields={[
                { label: 'Search Filter', value: this.state.search || 'None' },
              ]}
              analysis="Source subscription counts and activity status"
            />

            {this.state.sources && this.state.sources.length > 0 && (
              <div className="pdf-chart-section">
                <h3>Top 20 Sources - Total Subscriptions</h3>
                <Chart style={{ height: '400px' }}>
                  <ChartTitle text="Source Subscription Comparison" />
                  <ChartCategoryAxis>
                    <ChartCategoryAxisItem
                      categories={this.state.sources.slice(0, 20).map((item: any) =>
                        item.name ? item.name.substring(0, 20) + '...' : (item.identifier ? item.identifier.substring(0, 20) + '...' : 'Unknown')
                      )}
                      labels={{ rotation: -45 }}
                    />
                  </ChartCategoryAxis>
                  <ChartSeries>
                    <ChartSeriesItem
                      type="column"
                      data={this.state.sources.slice(0, 20).map((item: any) => item.total_subscription || 0)}
                      name="Total Subscriptions"
                      color="#8fd1bb"
                    />
                  </ChartSeries>
                </Chart>
              </div>
            )}
          </div>

          <div className="pdf-table-section">
            <h3>Detailed Source Information</h3>
          </div>
        </div>
        <ExcelExport data={this.state.sources}
          fileName="RasaSourceReports.xlsx"
          ref={(exporter) => {this.xlsxExport = exporter}}>
        <RasaDataGrid
            // Rasa Properties for entity, datasetName etc
            entityName="sourcesInfo"
            datasetName="sourcesInfo"
            datasetParams={[
              { param: 'filterVal', value: this.state.search },
            ]}
            editDefaultState={false}
            // events
            onDataReceived={this.dataReceived}
            sortable={true}
            pageable={true}
            pageSize={1000}
            data={[]}>
            <GridColumn field="identifier" title="Identifier" width={250}/>
            <GridColumn field="type" title="Type" width={100}/>
            <GridColumn field="name" title="Name" width={180}/>
            <GridColumn field="total_subscription" title="Subscriptions" width={120}/>
            <GridColumn field="last_post_date" title="Last Post Date" cell={DateTimeCell} width={120}/>
            <GridColumn field="last_success" title="Last Success" cell={DateTimeCell} width={120}/>
            <GridColumn field="last_failure" title="Last Failure" cell={DateTimeCell} width={120}/>
            <GridColumn field="last_failure_reason" title="Failure Reason" cell={FailureMsgCell}  width={120}/>
            <GridColumn field="updated" title="Updated" cell={DateTimeCell} width={120}/>
          </RasaDataGrid>
        </ExcelExport>
      </div>
    </div>
  </div>
  }

  protected dataReceived = (data) => {
		if (data.data) {
			this.setState({
				sources: data.data,
			})
		}
	}

  protected filterData = (s: any): boolean => {
    const lowerSearch = this.state.search.toLocaleLowerCase()
    return this.state.search.length <= 3 || (
      (s.name && s.name.toLowerCase().indexOf(lowerSearch) >= 0)
      || (s.id && s.id.toString().toLowerCase().indexOf(this.state.search.toLowerCase()) >= 0)
      || (s.type && s.type.toLowerCase().indexOf(this.state.search.toLowerCase()) >= 0)
      || (s.identifier && s.identifier.toLowerCase().indexOf(this.state.search.toLowerCase()) >= 0)
      || (s.last_failuree_reason && s.last_failuree_reason.toLowerCase().indexOf(this.state.search.toLowerCase()) >= 0)
      || (s.source_url && s.source_url.toLowerCase().indexOf(this.state.search.toLowerCase()) >= 0)
    )
  }

  private setSearch = (s: string) => {
    this.setState({
      search: s,
    })
  }
}
export const AnalyticsSourcesfailureReportTable = GenericRedux.registerNewDatasetContainerComponent(
  SourceReportTableComponent, 'sourcereports')
